import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ALPHA_ANALYZED,
  AnalysisType,
  ANALYZED_METRICS,
  ASSET_CLASSES,
  AVG_ACCURACY,
  AVG_HOLDING_PERIOD,
  AVG_LEV_ANALYZED,
  AVG_SLIPPAGE,
  AVG_WEIGHTED_ACCURACY,
  CURRENT_LEVERAGE,
  fetchAnalysis,
  fetchLeaderboard,
  fetchMetrics,
  LIMIT_BREAKING_ANALYZED,
  MAX_DD_ANALYZED,
  MAX_LEV_ANALYZED,
  ORDERS_ANALYZED,
  PlayerAnalysisType,
  PnLDistributionAnalysisType,
  PTF_RETURN_ANALYZED,
  recomputeAnalysis,
  recomputeLeaderboard,
  selectors,
  SHARPE_ANALYZED,
  VAR_ANALYZED,
  VOL_ANALYZED,
} from 'state';
import { Button, Table, Typography } from 'antd';
import { formatters } from 'services';
import {
  billionNoUnit1ExactDigit,
  commasExact1Digit,
  millionNoUnitExact2Digits,
  millionNoUnitNoDigits,
} from 'services/formatters';
import type { TableColumnGroupType as ColumnGroupType, TableColumnType as ColumnType } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import { analysisItemsSelector } from '../../../state/app/selectors';

const metricRenderers: Record<
  number,
  (value: number | null | undefined) => React.ReactNode
> = {
  [PTF_RETURN_ANALYZED]: v => formatters.percent2Digit(v ?? 0),
  [SHARPE_ANALYZED]: v => formatters.commasExact1Digit(v ?? 0),
  [ALPHA_ANALYZED]: v => formatters.percent2Digit(v ?? 0),
  [MAX_DD_ANALYZED]: v => formatters.percent2Digit(v ?? 0),
  [VAR_ANALYZED]: v => formatters.millionNoUnitExact2Digits(v ?? 0),
  [AVG_LEV_ANALYZED]: v => formatters.commasExact1Digit(v ?? 0),
  [MAX_LEV_ANALYZED]: v => formatters.commasExact1Digit(v ?? 0),
  [LIMIT_BREAKING_ANALYZED]: v => formatters.millionNoUnitExact2Digits(v ?? 0),
  [ORDERS_ANALYZED]: v => formatters.commasNoDigits(v ?? 0),
  [AVG_SLIPPAGE]: v => formatters.percent2Digit(v ?? 0),
  [AVG_ACCURACY]: v => formatters.percent2Digit(v ?? 0),
  [AVG_HOLDING_PERIOD]: v => formatters.commasExact1Digit(v ?? 0),
  [CURRENT_LEVERAGE]: v => formatters.commasExact1Digit(v ?? 0),
  [VOL_ANALYZED]: v => formatters.percent2Digit(v ?? 0),
  [AVG_WEIGHTED_ACCURACY]: v => formatters.percent2Digit(v ?? 0),
};

const AnalysisTable2: React.FC = () => {
  const dispatch = useDispatch();
  const waiting = useSelector(selectors.analysisWaiting);
  // const data = useSelector(selectors.analysis);

  let analysisItems = useSelector(selectors.analysisItemsSelector);

  useEffect(() => {
    dispatch(fetchAnalysis({ session_id: session.id }));
  }, [dispatch]);

  const session = useSelector(selectors.activeSession);
  const repCcy = useSelector(
    selectors.currencyById(session.reporting_currency)
  );

  const analyzedMetrics = useMemo(() => {
    const first = analysisItems?.[0];
    if (!first) return [];

    return Array.from(
      new Set(first?.analyzed_metrics?.map(s => s.analyzed_metric) ?? [])
    );
  }, [analysisItems]);

  const visibleAnalyzedMetrics = useMemo(
    () =>
      analyzedMetrics.filter(
        metric =>
          ![
            AVG_LEV_ANALYZED,
            MAX_LEV_ANALYZED,
            LIMIT_BREAKING_ANALYZED,
          ].includes(metric)
      ),
    [analyzedMetrics]
  );

  if (visibleAnalyzedMetrics.length === 0) {
    analysisItems = [];
  }

  const columns: ColumnsType<PlayerAnalysisType> = useMemo(() => {
    const base: ColumnsType<PlayerAnalysisType> = [
      {
        title: 'Player',
        dataIndex: 'player_name',
        key: 'player_name',
        // fixed: 'left',
      },
    ];

    const metricCols: ColumnsType<PlayerAnalysisType> =
      visibleAnalyzedMetrics.map(analyzed_metric => {
        // get label for this metric code (ANALYZED_METRICS is a choices tuple/array)
        // if ANALYZED_METRICS is like [[code, label], ...], build a map first
        // const metricLabelMap = Object.fromEntries(ANALYZED_METRICS);
        const title =
          ANALYZED_METRICS[analyzed_metric] ?? `Metric ${analyzed_metric}`;

        const renderFn = metricRenderers[analyzed_metric];

        return {
          title,
          key: `metric_${analyzed_metric}`,
          sorter: (a: PlayerAnalysisType, b: PlayerAnalysisType) => {
            const da = a.analyzed_metrics.find(
              s => s.analyzed_metric === analyzed_metric
            );
            const db = b.analyzed_metrics.find(
              s => s.analyzed_metric === analyzed_metric
            );

            const va = da?.value ?? 0;
            const vb = db?.value ?? 0;

            return va - vb; // ascending; use vb - va for descending
          },
          // defaultSortOrder:
          //   analyzed_metric === PTF_RETURN_ANALYZED ? 'descend' : undefined,
          render: (_: unknown, record: PlayerAnalysisType) => {
            const dp = record.analyzed_metrics.find(
              s => s.analyzed_metric === analyzed_metric
            );
            if (!dp) return '-';
            return renderFn(dp.value);
          },
        };
      });

    return [...base, ...metricCols];
  }, [visibleAnalyzedMetrics]);

  return (
    <div>
      <Typography.Title level={4}>Analysis Overview</Typography.Title>
      <div
        style={{
          width: 'fit-content', // shrink to content
          maxWidth: '100%', // don’t overflow viewport
          // margin: '0 auto', // center horizontally (optional)
        }}
      >
        <Table
          // scroll={{ x: true, y: '30vh' }}
          bordered
          loading={waiting}
          dataSource={analysisItems}
          columns={columns}
          footer={() => (
            <div>
              {/*<Button onClick={() => showEditWeightsModal({})}>*/}
              {/*  Edit Weights*/}
              {/*</Button>*/}
              <Button
                // disabled
                loading={waiting}
                onClick={() =>
                  dispatch(recomputeAnalysis({ session_id: session.id }))
                }
                style={{ marginLeft: 10 }}
              >
                Recompute
              </Button>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default AnalysisTable2;
